import { useCallback, useEffect, useRef, useState } from 'react'
import type { Map as MapLibreMap } from 'maplibre-gl'
import {
  TerraDraw, TerraDrawPolygonMode, TerraDrawRectangleMode, TerraDrawSelectMode,
} from 'terra-draw'
import { TerraDrawMapLibreGLAdapter } from 'terra-draw-maplibre-gl-adapter'
import { Alert, Box, Button, MenuItem, TextField, Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { Check, Hexagon, MousePointer2, RotateCcw, Search, Square, Trash2, TriangleAlert } from 'lucide-react'
import { useNavigate } from '../../lib/router'
import { ApiError } from '../../api/client'
import { useRunConfig } from '../../lib/runConfig'
import { useGoHome } from '../../lib/home'
import { AttributeName } from '../../components/AttributeName'
import { ReferenceFields, useMissingReferences } from '../../components/ReferenceFields'
import type { CheckDescriptor, Preview, ReferenceLayer, RunConfig, Supplier, Country } from '../../api/types'
import {
  useCreateRegion, useCreateRun, useNextPlotId, usePreviewCheck, usePrincipal, useRegions, useSaveDrawnPlots,
} from '../../api/hooks'
import { BasemapSwitch, MapCanvas, MapControl } from '../../components/MapCanvas'
import { CountryPicker } from '../../components/CountryPicker'
import { FarmerPicker, FoPicker, SupplierPicker, type RecordOption } from '../../components/pickers'
import { Mono, StatusPill } from '../../components/primitives'
import { useUnsavedWork } from '../../components/NavigationGuard'
import { centroid, parseCoordinates, perimeterM, polygonAreaHa, vertexCount } from '../../lib/geo'
import { ha, num } from '../../lib/format'
import { saveAs } from '../../lib/download'
import { STATUS_COLOURS, tokens } from '../../theme'

type Tool = 'polygon' | 'rectangle' | 'select'

interface DrawnPlot {
  /** terra-draw's feature id. */
  id: string
  geometry: GeoJSON.Polygon
  attributes: Attributes
  preview: Preview | null
  /** The live checks could not answer for this shape; it can be asked again. */
  previewError?: string
}

interface Attributes {
  Plot_ID: string
  Farmer_ID: string
  FO: string
  FO_ID: string
  Yield: string
  Certificate: string
  /** The records picked for FO and Farmer_ID; kept for the pickers, never sent. */
  _fo?: RecordOption | null
  _farmer?: RecordOption | null
}

/** What is sent for a plot: its attributes without the pickers' own state. */
function sendable(attributes: Attributes) {
  const { _fo, _farmer, ...rest } = attributes
  void _fo; void _farmer
  return rest
}

interface Props {
  checks: CheckDescriptor[]
  layers: ReferenceLayer[]
  suppliers: Supplier[]
  countries: Country[]
  defaultConfig: RunConfig
  switcher: React.ReactNode
}

const TOOLS: { tool: Tool; label: string; Icon: typeof Hexagon }[] = [
  { tool: 'polygon', label: 'Draw a polygon', Icon: Hexagon },
  { tool: 'rectangle', label: 'Draw a rectangle', Icon: Square },
  { tool: 'select', label: 'Edit vertices', Icon: MousePointer2 },
]

/** Teal while drawing — the design system's colour for an in-progress shape. */
const SHARED_STYLE = {
  fillColor: tokens.color.brandTeal,
  fillOpacity: 0.14,
  outlineColor: tokens.color.brandTeal,
  outlineWidth: 2,
} as const

const POLYGON_STYLE = {
  ...SHARED_STYLE,
  closingPointColor: tokens.color.brandTeal,
  closingPointWidth: 5,
  closingPointOutlineColor: tokens.color.ink0,
  closingPointOutlineWidth: 2,
  coordinatePointColor: tokens.color.ink0,
  coordinatePointWidth: 4,
  coordinatePointOutlineColor: tokens.color.brandTeal,
  coordinatePointOutlineWidth: 2,
} as const

const RECTANGLE_STYLE = {
  ...SHARED_STYLE,
  coordinatePointColor: tokens.color.ink0,
  coordinatePointWidth: 4,
  coordinatePointOutlineColor: tokens.color.brandTeal,
  coordinatePointOutlineWidth: 2,
} as const

/** Screen 2a — draw a handful of plots and see the checks answer as you go. */
export function DrawRun({ layers, suppliers, countries, defaultConfig, switcher }: Props) {
  const navigate = useNavigate()
  const createRegion = useCreateRegion()
  const saveDrawn = useSaveDrawnPlots()
  const createRun = useCreateRun()
  const preview = usePreviewCheck()
  // The way out, as on the upload screen (AG-92). Unsaved plots make the
  // guard ask first.
  const goHome = useGoHome()
  const qc = useQueryClient()

  /**
   * Where the plots go: a new region (named after the supplier and the time),
   * or one that already exists — the region "Add polygons" was opened from,
   * by default. Each drawing used to start a region of its own, so a second
   * plot looked as though it had replaced the first (AG-84).
   */
  const [params] = useSearchParams()
  const { data: regions = [] } = useRegions()
  const [targetRegion, setTargetRegion] = useState(params.get('region') ?? '')
  const target = regions.find((r) => r.id === targetRegion)

  // Numbered on from the organisation's highest P-nnn, so drawing again
  // never hands out a Plot_ID already in use.
  const { data: nextId } = useNextPlotId()
  const firstFree = nextId?.number ?? 1
  // The checks chosen on the upload screen, so drawn plots are judged alike.
  const { config: runConfig } = useRunConfig(defaultConfig)
  // The host's own references (a contract), for every plot saved together.
  const [refs, setRefs] = useState<Record<string, string>>({})
  const missingRefs = useMissingReferences(refs)

  const mapRef = useRef<MapLibreMap | null>(null)
  const drawRef = useRef<TerraDraw | null>(null)

  const [basemap, setBasemap] = useState<'map' | 'satellite'>('map')
  const [tool, setTool] = useState<Tool>('polygon')
  const [plots, setPlots] = useState<DrawnPlot[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [inProgress, setInProgress] = useState<{ points: number; areaHa: number; perimeterM: number } | null>(null)
  const [search, setSearch] = useState('')
  const [searchError, setSearchError] = useState('')

  const [countryCode, setCountryCode] = useState('CI')
  // Chosen from the records, or added there and then: drawn plots have no
  // file to read SU from, so this is where their supplier comes from.
  const [supplier, setSupplier] = useState<RecordOption | null>(null)
  const supplierTouched = useRef(false)
  const supplierLabel = supplier?.label ?? ''
  const knownSupplier = supplier ? { id: supplier.id } : undefined

  // Start on a supplier from this country, until the field has been edited.
  useEffect(() => {
    if (supplierTouched.current || supplier || !suppliers.length) return
    const match = suppliers.find((s) => s.country_code === countryCode)
    if (match) setSupplier({ id: match.id, label: match.name, detail: match.country_code })
  }, [suppliers, supplier, countryCode])

  // Farmer and FO carry over from the previous plot; the run settings supply the rest.
  // A farmer's own login draws their own fields: they start as that farmer.
  const { data: principal } = usePrincipal()
  const self = principal?.user?.kind === 'farmer' ? principal.user.farmers?.[0] : undefined
  const selfOption: RecordOption | undefined = self ? { id: self.id, label: self.name || self.code || '—', code: self.code } : undefined
  const [draft, setDraft] = useState<Attributes>({
    Plot_ID: 'P-001', Farmer_ID: self?.code ?? '', FO: '', FO_ID: '', Yield: '', Certificate: '',
    ...(selfOption ? { _farmer: selfOption } : {}),
  })
  // The session may arrive after the first render.
  useEffect(() => {
    if (selfOption && !draftRef.current._farmer) setDraft((d) => ({ ...d, _farmer: selfOption, Farmer_ID: self?.code ?? '' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [self?.id])
  const draftRef = useRef(draft)
  draftRef.current = draft

  /**
   * The next free P-nnn: one past the highest in use. Counting the plots
   * instead gave an undone or deleted plot's successor a reference already
   * taken (AG-84).
   */
  const nextPlotRef = (current: DrawnPlot[]) => {
    const highest = current.reduce((n, p) => {
      const m = /^P-(\d+)$/.exec(p.attributes.Plot_ID.trim())
      return m ? Math.max(n, Number(m[1])) : n
    }, firstFreeRef.current - 1)
    return `P-${String(highest + 1).padStart(3, '0')}`
  }
  // Read by the map's handlers, which are wired once.
  const firstFreeRef = useRef(firstFree)
  firstFreeRef.current = firstFree
  // The first plot's suggested id, once the organisation's is known.
  useEffect(() => {
    if (!nextId) return
    setDraft((d) => (d.Plot_ID === 'P-001' && !plotsRef.current.length ? { ...d, Plot_ID: nextId.plot_id } : d))
  }, [nextId])

  // The other plots on screen travel with every preview, so two shapes that
  // plainly cross are reported as overlapping before either is saved.
  const plotsRef = useRef<DrawnPlot[]>([])

  /**
   * Asks the live checks about one plot and writes the answer onto it. Each
   * call settles on its own: through the mutation's shared callbacks only
   * the latest call's answer arrived, so a plot closed while another was
   * being checked stayed "checking…" for good (AG-83).
   */
  const runPreview = useCallback(
    (id: string, geometry: GeoJSON.Polygon, attributes: Attributes, checkOverlaps: boolean) => {
      const settle = (change: Partial<DrawnPlot>) =>
        setPlots((cur) => cur.map((x) => (x.id === id ? { ...x, ...change } : x)))
      settle({ preview: null, previewError: undefined })
      preview.mutateAsync(
        {
          geometry,
          properties: {
            CO: countryCode, SU: supplierLabel,
            ...sendable(attributes),
          },
          country_code: countryCode,
          supplier_id: knownSupplier?.id ?? null,
          check_overlaps: checkOverlaps,
          session_plots: plotsRef.current
            .filter((p) => p.id !== id)
            .map((p) => ({ ref: p.attributes.Plot_ID || p.id, geometry: p.geometry })),
        },
      )
        .then((p) => settle({ preview: p }))
        .catch((err: Error) => settle({ previewError: err.message || 'The checks did not answer.' }))
    },
    // `preview` is a stable mutation object from TanStack Query.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [countryCode, supplierLabel, knownSupplier?.id],
  )

  // The map's handlers are wired once; through this they use the current
  // country and supplier, not those of the first render.
  const runPreviewRef = useRef(runPreview)
  runPreviewRef.current = runPreview

  // --- terra-draw wiring --------------------------------------------------
  const handleReady = useCallback((map: MapLibreMap) => {
    mapRef.current = map

    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPolygonMode({ styles: POLYGON_STYLE, showCoordinatePoints: true }),
        new TerraDrawRectangleMode({ styles: RECTANGLE_STYLE }),
        new TerraDrawSelectMode({
          flags: {
            polygon: {
              feature: {
                draggable: true,
                coordinates: { midpoints: true, draggable: true, deletable: true },
              },
            },
            rectangle: {
              feature: { draggable: true, coordinates: { draggable: true } },
            },
          },
        }),
      ],
    })
    drawRef.current = draw
    draw.start()
    draw.setMode('polygon')

    // Live tooltip while a shape is open: local maths, no round trip.
    // terra-draw marks the open shape with `currentlyDrawing`, which is what
    // separates the tooltip's live figures from a finished plot's.
    // This fires on every pointer move, so it reads only the features that
    // changed (not a copy of the whole store) and updates at most once a
    // frame: doing more froze the screen while drawing (AG-85).
    let frame = 0
    draw.on('change', (ids, type) => {
      if (type === 'delete') {
        // Esc, or leaving the tool: the open shape has gone.
        if (!draw.getSnapshot().some((f) => f.properties?.['currentlyDrawing'] === true)) setInProgress(null)
        return
      }
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const open = ids
          .map((id) => draw.getSnapshotFeature(id))
          .find((f) => f?.properties?.['currentlyDrawing'] === true)
        const geometry = open?.geometry as GeoJSON.Polygon | undefined
        if (!geometry || geometry.type !== 'Polygon') return
        setInProgress({
          points: vertexCount(geometry),
          areaHa: polygonAreaHa(geometry),
          perimeterM: perimeterM(geometry),
        })
      })
    })

    // Closing the shape is what runs the full set, overlap included.
    draw.on('finish', (id, context) => {
      if (context.action === 'draw') setInProgress(null)

      const feature = draw.getSnapshotFeature(id)
      const geometry = feature?.geometry as GeoJSON.Polygon | undefined
      if (!geometry || geometry.type !== 'Polygon') return

      // The request goes out here, not inside the state updater, which React
      // may run twice.
      const current = plotsRef.current
      const existing = current.find((p) => p.id === String(id))
      const attributes: Attributes = existing?.attributes ?? {
        ...draftRef.current,
        Plot_ID: draftRef.current.Plot_ID || nextPlotRef(current),
      }
      const next = existing
        ? current.map((p) => (p.id === String(id) ? { ...p, geometry, preview: null } : p))
        : [...current, { id: String(id), geometry, attributes, preview: null }]
      plotsRef.current = next
      setPlots(next)
      runPreviewRef.current(String(id), geometry, attributes, true)
      setActiveId(String(id))
      // Carry the farmer and organisation into the next plot, as 2a describes.
      setDraft((d) => ({ ...d, Plot_ID: '', Yield: '', Certificate: '' }))
    })

    return () => draw.stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const draw = drawRef.current
    if (!draw) return
    draw.setMode(tool === 'select' ? 'select' : tool)
  }, [tool])

  useEffect(() => () => drawRef.current?.stop(), [])

  plotsRef.current = plots
  const active = plots.find((p) => p.id === activeId) ?? null

  // Drawn plots live in the browser until Validate saves them.
  useUnsavedWork(
    plots.length > 0 && !saveDrawn.isSuccess,
    `${plots.length} drawn plot${plots.length === 1 ? ' has' : 's have'} not been saved yet.`,
  )

  /** Re-runs the live checks for a plot after its attributes or shape change. */
  const refreshPlot = (id: string) => {
    const plot = plotsRef.current.find((p) => p.id === id)
    if (!plot) return
    runPreview(id, plot.geometry, plot.attributes, true)
  }

  /**
   * Changes attributes of the plot being edited — and re-checks it with them,
   * as leaving a text field does — or of the next plot to be drawn.
   */
  const setAttributes = (change: Partial<Attributes>) => {
    if (!active) return setDraft((d) => ({ ...d, ...change }))
    const attributes = { ...active.attributes, ...change }
    setPlots((cur) => cur.map((p) => (p.id === active.id ? { ...p, attributes } : p)))
    runPreview(active.id, active.geometry, attributes, true)
  }

  const removePlot = (id: string) => {
    drawRef.current?.removeFeatures([id])
    setPlots((cur) => cur.filter((p) => p.id !== id))
    if (activeId === id) setActiveId(null)
  }

  /**
   * Drops the shape being drawn. terra-draw does this on Esc, but only while
   * the map has the keyboard; re-entering the mode is what the Edit tool was
   * doing when it worked as the way out (AG-80).
   */
  const cancelShape = useCallback(() => {
    const draw = drawRef.current
    if (draw) draw.setMode(draw.getMode())
    setInProgress(null)
  }, [])
  useEffect(() => {
    if (!inProgress) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && cancelShape()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [inProgress, cancelShape])

  const undo = () => {
    const last = plots[plots.length - 1]
    if (last) removePlot(last.id)
  }

  const goToSearch = () => {
    const coords = parseCoordinates(search)
    if (!coords) {
      setSearchError('Enter coordinates as 5.7988, -6.6014')
      return
    }
    setSearchError('')
    mapRef.current?.flyTo({ center: coords, zoom: 15 })
  }

  const totalHa = plots.reduce((s, p) => s + (p.preview?.area_ha ?? polygonAreaHa(p.geometry)), 0)

  const exportGeoJson = () => {
    const collection: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: plots.map((p) => ({
        type: 'Feature',
        geometry: p.geometry,
        properties: {
          CO: countryCode, SU: supplierLabel,
          ...sendable(p.attributes),
          Yield: p.attributes.Yield === '' ? null : Number(p.attributes.Yield),
        },
      })),
    }
    saveAs(JSON.stringify(collection, null, 2), `drawn_plots_${countryCode}_${new Date().toISOString().slice(0, 10)}.geojson`, 'application/geo+json')
  }

  /**
   * Drawn plots need somewhere to live, and this screen is for a handful at
   * a time, so it makes the region rather than asking. It is named after
   * the supplier, the day and the time — the day alone collided with the
   * day's earlier drawing, and the refusal went unseen (AG-83) — and can be
   * renamed like any other. What an attempt got done before failing — the
   * region made, the plots saved into it — is not done twice by the next.
   */
  const madeRegion = useRef<string | null>(null)
  const savedInto = useRef<string | null>(null)
  const [validating, setValidating] = useState(false)
  const [validateError, setValidateError] = useState<string | null>(null)
  const regionName = (attempt: number) => {
    const now = new Date()
    const stamp = `${now.toISOString().slice(0, 10)} ${now.toTimeString().slice(0, 5)}`
    return `${supplierLabel || 'Drawn plots'} — ${stamp}${attempt > 1 ? ` (${attempt})` : ''}`
  }

  const validate = async () => {
    setValidating(true)
    setValidateError(null)
    try {
      let regionId = targetRegion || madeRegion.current
      for (let attempt = 1; !regionId; attempt++) {
        try {
          const region = await createRegion.mutateAsync({
            name: regionName(attempt), country_code: countryCode, description: 'Polygons drawn in the browser.',
          })
          regionId = region.id
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 409) || attempt >= 5) throw err
        }
      }
      madeRegion.current = regionId
      if (savedInto.current !== regionId) await saveDrawn.mutateAsync({
        regionId,
        supplier_id: knownSupplier?.id ?? null,
        supplier_name: supplierLabel || null,
        country_code: countryCode,
        refs: Object.fromEntries(Object.entries(refs).filter(([, v]) => v)),
        plots: plots.map((p) => ({
          geometry: p.geometry,
          properties: {
            CO: countryCode, SU: supplierLabel,
            ...sendable(p.attributes),
            Yield: p.attributes.Yield === '' ? null : Number(p.attributes.Yield),
          },
        })),
      })
      savedInto.current = regionId
      await createRun.mutateAsync({ region_id: regionId, config: runConfig })
      qc.invalidateQueries({ queryKey: ['next-plot-id'] })
      navigate(`/regions/${regionId}`)
    } catch (err) {
      setValidateError(`The plots were not validated: ${(err as Error).message}`)
    } finally {
      setValidating(false)
    }
  }

  // Plot_ID is how a plot is told apart; two the same are both flagged
  // after the run, so say so while it can still be fixed (AG-84).
  const refKey = (p: DrawnPlot) => p.attributes.Plot_ID.trim().toLowerCase()
  const duplicates = plots.filter((p, i) => refKey(p) && plots.findIndex((q) => refKey(q) === refKey(p)) !== i)
  const duplicateRefs = new Set(duplicates.map(refKey))
  const isDuplicate = (p: DrawnPlot) => duplicateRefs.has(refKey(p))

  const boundaryLayer = layers.find((l) => l.kind === 'country_boundary' && l.is_default)

  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Mode switch, country and supplier — the run settings drawn plots inherit. */}
      <Box
        sx={{
          flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 6, px: 6, py: 4, backgroundColor: 'background.paper',
          borderBottom: `1px solid ${tokens.color.ink200}`,
        }}
      >
        {switcher}
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
          <Box sx={{ minWidth: 240 }}>
            <TextField
              select
              size="small"
              fullWidth
              label="Save into"
              value={targetRegion}
              disabled={validating}
              onChange={(e) => {
                setTargetRegion(e.target.value)
                // A region's plots are judged against its country.
                const r = regions.find((x) => x.id === e.target.value)
                if (r?.country_code) setCountryCode(r.country_code)
              }}
            >
              <MenuItem value="">A new region</MenuItem>
              {regions.map((r) => <MenuItem key={r.id} value={r.id}>{r.name}</MenuItem>)}
            </TextField>
          </Box>
          <Box sx={{ minWidth: 220 }}>
            <CountryPicker
              countries={countries}
              value={countryCode}
              onChange={setCountryCode}
              label="Country"
            />
          </Box>
          <Box sx={{ minWidth: 260 }}>
            <SupplierPicker
              value={supplier}
              countryCode={countryCode}
              onChange={(o) => { supplierTouched.current = true; setSupplier(o) }}
            />
          </Box>
        </Box>
      </Box>

      <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
        <MapCanvas onReady={handleReady} basemap={basemap} initialZoom={14} cursor={tool === 'select' ? '' : 'crosshair'}>
          {/* Tools */}
          <MapControl sx={{ top: 16, left: 16, flexDirection: 'column' }}>
            {TOOLS.map(({ tool: t, label, Icon }, i) => (
              <Box
                key={t}
                component="button"
                type="button"
                aria-label={label}
                aria-pressed={tool === t}
                title={label}
                onClick={() => setTool(t)}
                sx={{
                  width: 36, height: 36, border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
                  borderTop: i > 0 ? `1px solid ${tokens.color.ink200}` : 0,
                  backgroundColor: tool === t ? tokens.color.brandTeal100 : 'transparent',
                  color: tool === t ? tokens.color.brandTeal : tokens.color.ink500,
                  '&:hover': { backgroundColor: tool === t ? tokens.color.brandTeal100 : tokens.color.ink100 },
                }}
              >
                <Icon size={18} strokeWidth={1.5} />
              </Box>
            ))}
            <Box
              component="button" type="button" aria-label="Undo last plot" title="Undo last plot" onClick={undo}
              sx={{
                width: 36, height: 36, border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
                borderTop: `1px solid ${tokens.color.ink200}`, backgroundColor: 'transparent', color: tokens.color.ink500,
                '&:hover': { backgroundColor: tokens.color.ink100 },
              }}
            >
              <RotateCcw size={18} strokeWidth={1.5} />
            </Box>
            <Box
              component="button" type="button" aria-label="Delete the selected plot" title="Delete the selected plot"
              onClick={() => activeId && removePlot(activeId)}
              sx={{
                width: 36, height: 36, border: 0, cursor: 'pointer', display: 'grid', placeItems: 'center',
                borderTop: `1px solid ${tokens.color.ink200}`, backgroundColor: 'transparent', color: tokens.color.ink500,
                '&:hover': { backgroundColor: tokens.color.ink100 },
              }}
            >
              <Trash2 size={18} strokeWidth={1.5} />
            </Box>
          </MapControl>

          {/* Place and coordinate search */}
          <MapControl sx={{ top: 16, left: 68, width: 320, height: 36, alignItems: 'center', px: 3, gap: 2 }}>
            <Search size={16} strokeWidth={1.5} color={tokens.color.ink400} />
            <Box
              component="input"
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              onKeyDown={(e) => e.key === 'Enter' && goToSearch()}
              placeholder="Search a village or paste coordinates"
              aria-label="Search a village or paste coordinates"
              sx={{
                flex: 1, border: 0, outline: 'none', backgroundColor: 'transparent',
                font: `400 13px ${tokens.font.sans}`, color: 'text.primary',
                '&::placeholder': { color: tokens.color.ink400 },
              }}
            />
          </MapControl>
          {searchError && (
            <Box sx={{ position: 'absolute', top: 58, left: 68, width: 320 }}>
              <Alert severity="warning" sx={{ py: 0.5 }}>{searchError}</Alert>
            </Box>
          )}

          <BasemapSwitch
            value={basemap}
            onChange={setBasemap}
            options={[{ value: 'map', label: 'Basemap' }, { value: 'satellite', label: 'Satellite' }]}
          />

          {/* Live tooltip while a shape is open */}
          {inProgress && inProgress.points > 0 && (
            <Box
              sx={{
                position: 'absolute', left: '50%', top: 72, transform: 'translateX(-50%)',
                px: 2.5, py: 2, borderRadius: `${tokens.radius.md}px`,
                backgroundColor: tokens.color.brandDeep900, color: tokens.color.ink0,
                boxShadow: tokens.shadow[3], pointerEvents: 'none',
              }}
            >
              <Typography sx={{ font: `500 12px ${tokens.font.sans}` }}>
                {tool === 'rectangle' ? 'Drag to size the rectangle' : 'Click the first point to close'}
              </Typography>
              <Mono sx={{ fontSize: 11, opacity: 0.75 }}>
                {inProgress.points} points · {num(inProgress.areaHa, 2)} ha · perimeter {num(inProgress.perimeterM, 0)} m
              </Mono>
            </Box>
          )}

          {/* Keyboard hints, as in the mockup */}
          <Box
            sx={{
              position: 'absolute', left: 16, bottom: 16, display: 'flex', gap: 2, alignItems: 'center',
              px: 3, py: 2, backgroundColor: 'background.paper', border: `1px solid ${tokens.color.ink200}`,
              borderRadius: `${tokens.radius.md}px`, font: `400 12px ${tokens.font.sans}`, color: 'text.secondary',
            }}
          >
            {inProgress ? (
              <Box
                component="button" type="button" onClick={cancelShape}
                sx={{
                  border: 0, background: 'none', p: 0, cursor: 'pointer', display: 'flex', gap: 2, alignItems: 'center',
                  font: 'inherit', color: tokens.color.brandTeal,
                }}
              >
                <Key>Esc</Key> Cancel shape
              </Box>
            ) : (
              <><Key>Esc</Key> cancel shape</>
            )}
          </Box>
        </MapCanvas>

        {/* Right panel: the plots drawn so far, and the active plot's attributes */}
        <Box
          component="aside"
          sx={{
            width: 380, flex: 'none', backgroundColor: 'background.paper',
            borderLeft: `1px solid ${tokens.color.ink200}`, display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 5, py: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
              borderBottom: `1px solid ${tokens.color.ink200}`,
            }}
          >
            <Typography variant="h4">Plots</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
              {num(plots.length)} drawn · {num(totalHa, 2)} ha
            </Typography>
          </Box>

          <Box sx={{ overflowY: 'auto', flex: '0 1 auto' }}>
            {plots.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.secondary', px: 5, py: 5 }}>
                Plots you draw appear here. Checks run as you close each shape.
              </Typography>
            )}
            {plots.map((plot) => {
              const status = plot.preview?.status ?? 'passed'
              const colours = STATUS_COLOURS[status]
              const failures = plot.preview?.results.filter((r) => r.status === 'fail') ?? []
              return (
                <Box key={plot.id}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => {
                      setActiveId(plot.id)
                      const c = centroid(plot.geometry)
                      if (c) mapRef.current?.easeTo({ center: c })
                    }}
                    sx={{
                      width: '100%', textAlign: 'left', border: 0, cursor: 'pointer',
                      px: 5, py: 3, display: 'flex', alignItems: 'center', gap: 3,
                      borderBottom: `1px solid ${tokens.color.ink200}`,
                      backgroundColor: activeId === plot.id ? tokens.color.ink50 : 'transparent',
                      '&:hover': { backgroundColor: tokens.color.ink50 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10, height: 10, flex: 'none', borderRadius: `${tokens.radius.xs}px`,
                        backgroundColor: colours.fill, border: `1px solid ${colours.stroke}`,
                      }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Mono sx={{ display: 'block', fontSize: 13, fontWeight: 500 }}>{plot.attributes.Plot_ID || '—'}</Mono>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {ha(plot.preview?.area_ha ?? polygonAreaHa(plot.geometry))}
                        {plot.attributes.Farmer_ID ? ` · Farmer ${plot.attributes.Farmer_ID}` : ''}
                      </Typography>
                    </Box>
                    {isDuplicate(plot) ? (
                      <StatusPill status="flagged" label="Duplicate Plot_ID" />
                    ) : plot.previewError ? (
                      <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        title={plot.previewError}
                        onClick={(e) => { e.stopPropagation(); refreshPlot(plot.id) }}
                        sx={{ font: `500 12px ${tokens.font.sans}`, color: tokens.color.danger, textDecoration: 'underline', textUnderlineOffset: 2 }}
                      >
                        Checks failed · retry
                      </Box>
                    ) : plot.preview === null ? (
                      <Typography variant="caption" sx={{ color: 'text.disabled' }}>checking…</Typography>
                    ) : failures.length === 0 ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: tokens.color.success, font: `500 12px ${tokens.font.sans}` }}>
                        <Check size={12} strokeWidth={2.5} />
                        All checks pass
                      </Box>
                    ) : (
                      <StatusPill
                        status={status}
                        label={`${failures.length} ${status === 'rejected' ? (failures.length === 1 ? 'failure' : 'failures') : failures.length === 1 ? 'flag' : 'flags'}`}
                      />
                    )}
                  </Box>

                  {activeId === plot.id && failures.length > 0 && (
                    <Box sx={{ px: 5, py: 3, display: 'flex', flexDirection: 'column', gap: 2.5, borderBottom: `1px solid ${tokens.color.ink200}` }}>
                      {failures.map((f) => (
                        <Box key={f.key} sx={{ display: 'flex', gap: 2.5, alignItems: 'flex-start' }}>
                          <Box sx={{ color: f.tier === 'compliance' ? tokens.color.danger : tokens.color.warning, mt: 0.5, flex: 'none' }}>
                            <TriangleAlert size={16} strokeWidth={1.5} />
                          </Box>
                          <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.45 }}>
                            {f.message}
                            {f.key === 'partial_overlap' && f.detail?.overlaps?.[0] && (
                              <>
                                {' '}
                                <Box
                                  component="button"
                                  type="button"
                                  onClick={() => {
                                    const snapped = snapToNeighbour(drawRef.current, plot, f.detail.overlaps[0])
                                    if (!snapped) return
                                    const next = plotsRef.current.map((p) => (p.id === plot.id ? { ...p, ...snapped, preview: null } : p))
                                    plotsRef.current = next
                                    setPlots(next)
                                    if (activeId === plot.id) setActiveId(snapped.id)
                                    runPreview(snapped.id, snapped.geometry, plot.attributes, true)
                                  }}
                                  sx={{
                                    border: 0, background: 'none', p: 0, cursor: 'pointer',
                                    color: tokens.color.brandTeal, font: `500 13px ${tokens.font.sans}`,
                                    textDecoration: 'underline', textUnderlineOffset: 2,
                                  }}
                                >
                                  Snap to {f.detail.overlaps[0].plot_ref ?? 'neighbour'}
                                </Box>
                              </>
                            )}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )
            })}
          </Box>

          {/* Attribute form for the active plot, or the next one to be drawn */}
          <Box sx={{ px: 5, py: 4, borderTop: `1px solid ${tokens.color.ink200}`, mt: 'auto', display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: 'text.disabled' }}>
                {active ? `Editing · ${active.attributes.Plot_ID || 'plot'}` : `Drawing · ${draft.Plot_ID || nextPlotRef(plots)}`}
              </Typography>
              <Typography variant="caption" sx={{ color: tokens.color.brandTeal }}>
                {active ? 'saved' : 'in progress'}
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2.5 }}>
              {(['Plot_ID', 'Yield'] as const).map((field) => (
                <Box key={field}>
                  <Typography sx={{ font: `500 13px ${tokens.font.sans}`, mb: 1.5 }}>
                    <AttributeName field={field} />
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    type={field === 'Yield' ? 'number' : 'text'}
                    value={active ? active.attributes[field] : draft[field]}
                    placeholder={field === 'Yield' ? '0' : ''}
                    inputProps={{ 'aria-label': field }}
                    InputProps={{
                      endAdornment: field === 'Yield' ? <Typography variant="caption" sx={{ color: 'text.disabled' }}>kg</Typography> : undefined,
                      sx: { fontFamily: field === 'Yield' ? tokens.font.sans : tokens.font.mono, fontSize: 13 },
                    }}
                    onChange={(e) => {
                      const value = e.target.value
                      if (active) {
                        setPlots((cur) => cur.map((p) => (p.id === active.id ? { ...p, attributes: { ...p.attributes, [field]: value } } : p)))
                      } else {
                        setDraft((d) => ({ ...d, [field]: value }))
                      }
                    }}
                    onBlur={() => active && refreshPlot(active.id)}
                  />
                </Box>
              ))}
              {/* Farmer organisation and farmer are records: picked, or added
                  here. FO_ID is the picked organisation's. */}
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ font: `500 13px ${tokens.font.sans}`, mb: 1.5 }}>
                  <AttributeName field="FO" />{(active ? active.attributes.FO_ID : draft.FO_ID) ? (
                    <Mono sx={{ ml: 1.5, fontSize: 12, color: 'text.secondary' }}>FO_ID {active ? active.attributes.FO_ID : draft.FO_ID}</Mono>
                  ) : null}
                </Typography>
                <FoPicker
                  value={(active ? active.attributes : draft)._fo ?? null}
                  countryCode={countryCode}
                  supplierId={knownSupplier?.id}
                  onChange={(o) => setAttributes({ _fo: o, FO: o?.label ?? '', FO_ID: o?.code ?? '' })}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ font: `500 13px ${tokens.font.sans}`, mb: 1.5 }}><AttributeName field="Farmer_ID" /></Typography>
                <FarmerPicker
                  value={(active ? active.attributes : draft)._farmer ?? null}
                  supplierId={knownSupplier?.id}
                  foId={(active ? active.attributes : draft)._fo?.id}
                  onChange={(o) => setAttributes({ _farmer: o, Farmer_ID: o?.code ?? o?.label ?? '' })}
                />
              </Box>
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography sx={{ font: `500 13px ${tokens.font.sans}`, mb: 1.5 }}><AttributeName field="Certificate" /></Typography>
                <TextField
                  fullWidth size="small"
                  value={active ? active.attributes.Certificate : draft.Certificate}
                  placeholder="e.g. RA-CI-2025-0412"
                  inputProps={{ 'aria-label': 'Certificate' }}
                  onChange={(e) => {
                    const value = e.target.value
                    if (active) {
                      setPlots((cur) => cur.map((p) => (p.id === active.id ? { ...p, attributes: { ...p.attributes, Certificate: value } } : p)))
                    } else {
                      setDraft((d) => ({ ...d, Certificate: value }))
                    }
                  }}
                  onBlur={() => active && refreshPlot(active.id)}
                />
              </Box>
            </Box>

            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              CO and SU are filled from the run settings. Farmer and FO carry over from the last plot.
            </Typography>

            <ReferenceFields dense values={refs} onChange={(key, value) => setRefs((r) => ({ ...r, [key]: value ?? '' }))} />

            <LiveChecks preview={active?.preview ?? null} inProgress={inProgress} boundary={boundaryLayer?.name} />
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 'none', height: 64, borderTop: `1px solid ${tokens.color.ink200}`,
          backgroundColor: 'background.paper', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', px: 6,
        }}
      >
        {validateError ? (
          <Alert severity="error" sx={{ py: 0, mr: 4 }} onClose={() => setValidateError(null)}>{validateError}</Alert>
        ) : missingRefs.length > 0 && plots.length > 0 ? (
          <Alert severity="warning" sx={{ py: 0, mr: 4 }}>Add the {missingRefs.join(', ')} to continue.</Alert>
        ) : duplicateRefs.size > 0 ? (
          <Alert severity="warning" sx={{ py: 0, mr: 4 }}>
            Two plots share a Plot_ID ({[...new Set(duplicates.map((p) => p.attributes.Plot_ID.trim()))].join(', ')}). Give each its own to continue.
          </Alert>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {target
              ? <>Validate adds these to {target.name}. Drawn plots are WGS-84 (EPSG:4326).</>
              : <>Validate saves these into a new region, “{regionName(1)}”. Drawn plots are WGS-84 (EPSG:4326).</>}
          </Typography>
        )}
        <Box sx={{ display: 'flex', gap: 2, flex: 'none' }}>
          <Button variant="text" onClick={() => goHome()} disabled={validating}>Cancel</Button>
          <Button variant="outlined" onClick={exportGeoJson} disabled={!plots.length}>Export GeoJSON</Button>
          <Button onClick={validate} disabled={!plots.length || validating || duplicateRefs.size > 0 || missingRefs.length > 0}>
            {validating ? 'Starting…' : `Validate ${plots.length} plot${plots.length === 1 ? '' : 's'}`}
          </Button>
        </Box>
      </Box>
    </Box>
  )
}

function Key({ children, sx }: { children: React.ReactNode; sx?: React.ComponentProps<typeof Box>['sx'] }) {
  return (
    <Box
      component="span"
      sx={{
        font: `500 11px ${tokens.font.mono}`, px: 1.5, py: 0.5, borderRadius: `${tokens.radius.sm}px`,
        backgroundColor: tokens.color.ink100, color: 'text.primary', ...sx,
      }}
    >
      {children}
    </Box>
  )
}

/** The live-checks readout at the foot of the side panel. */
function LiveChecks({
  preview, inProgress, boundary,
}: { preview: Preview | null; inProgress: { areaHa: number } | null; boundary?: string }) {
  const row = (label: string, value: React.ReactNode, tone: 'ok' | 'bad' | 'muted' = 'muted') => (
    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography
        variant="caption"
        sx={{
          fontWeight: tone === 'muted' ? 400 : 500, textAlign: 'right',
          color: tone === 'ok' ? tokens.color.success : tone === 'bad' ? tokens.color.danger : 'text.disabled',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {value}
      </Typography>
    </Box>
  )

  return (
    <Box
      sx={{
        mt: 'auto', p: 3, borderRadius: `${tokens.radius.md}px`, backgroundColor: tokens.color.ink100,
        display: 'flex', flexDirection: 'column', gap: 1.5,
      }}
    >
      <Typography sx={{ font: `500 12px ${tokens.font.sans}`, mb: 0.5 }}>Live checks</Typography>
      {!preview && !inProgress && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Draw or select a plot to see its checks.
        </Typography>
      )}
      {!preview && inProgress && row('Area', `${num(inProgress.areaHa, 2)} ha`)}
      {preview?.results
        .filter((r) => r.key !== 'null_fields')
        .map((r) =>
          row(
            r.label,
            r.message,
            // Not judged (outside a layer's coverage) reads like pending: neither a pass nor a failure.
            r.status === 'pending' || r.status === 'unknown' ? 'muted' : r.status === 'pass' ? 'ok' : 'bad',
          ),
        )}
      {boundary && preview && (
        <Typography variant="caption" sx={{ color: 'text.disabled', mt: 0.5 }}>
          Boundary: {boundary.replace(/ \(.*\)$/, '')}
        </Typography>
      )}
    </Box>
  )
}

/**
 * "Snap to neighbour": pulls the drawn plot's vertices that sit inside the
 * neighbour back onto the shared edge, clearing a small overlap without
 * redrawing the shape.
 */
function snapToNeighbour(
  draw: TerraDraw | null,
  plot: DrawnPlot,
  overlap: { plot_id: string; plot_ref: string | null; overlap_ha: number },
): { id: string; geometry: GeoJSON.Polygon } | null {
  if (!draw) return null
  // Shrink the plot very slightly towards its own centre: enough to release a
  // few-percent overlap, and the server re-checks the result immediately.
  const c = centroid(plot.geometry)
  if (!c) return null
  const shrink = 1 - Math.min(0.08, Math.max(0.01, overlap.overlap_ha / Math.max(0.01, polygonAreaHa(plot.geometry))))
  const geometry: GeoJSON.Polygon = {
    type: 'Polygon',
    coordinates: (plot.geometry.coordinates as [number, number][][]).map((ring) =>
      ring.map(([lon, lat]) => [c[0] + (lon - c[0]) * shrink, c[1] + (lat - c[1]) * shrink] as [number, number]),
    ),
  }
  const newId = String(draw.getFeatureId())
  draw.removeFeatures([plot.id])
  draw.addFeatures([
    { id: newId, type: 'Feature', geometry, properties: { mode: 'polygon' } } as never,
  ])
  return { id: newId, geometry }
}
